import { useEffect, useRef, useState } from 'react';
import { fichierDocumentPrepare } from '../api/documents.js';
import { envoisApi, piecesApi } from '../api/resources.js';
import { attestationsAEnvoyer, iconeAttestation, nomAttestation } from '../lib/attestations';
import { adresseApercuLien, composeMessage, liensEnvoi } from '../lib/envoi';
import { tr, trf } from '../lib/i18n';
import { C } from '../theme/palette';
import { useLienClient } from './RelanceParLien.jsx';

/** Vrai si le téléphone sait partager ce fichier (iPhone, Android). */
const partagePossible = (fichier) => {
  try {
    return (
      typeof navigator !== 'undefined' &&
      typeof navigator.share === 'function' &&
      typeof navigator.canShare === 'function' &&
      navigator.canShare({ files: [fichier] })
    );
  } catch {
    return false;
  }
};

/**
 * Feuille d'envoi d'une pièce au client.
 *
 * Elle monte du bas de l'écran, comme les partages du téléphone : c'est le
 * geste que l'artisan connaît. Chaque canal est un lien ordinaire plutôt qu'un
 * appel de fonction — le système d'exploitation sait mieux que nous quelle
 * application ouvrir, et le lien fonctionne même si celle qu'on attendait
 * n'est pas installée.
 *
 * En dernier, « Autre » ouvre le partage du téléphone (Telegram, Viber,
 * Signal…) ou, à défaut, copie le message avec une confirmation.
 *
 * S'y ajoute, en tête, l'envoi par le serveur : SwissTech expédie lui-même le
 * courriel, PDF officiel joint, et l'inscrit au journal des envois. C'est le
 * seul canal où l'application sait que le message est parti.
 *
 * @param {object} props propriétés de la feuille
 * @param {object} props.piece devis ou facture
 * @param {string} props.nature « Devis », « Offre », « Facture »
 * @param {object} [props.client] destinataire
 * @param {object} [props.soc] coordonnées de l'entreprise
 * @param {() => void} props.onClose fermeture de la feuille
 * @param {(canal: string) => void} [props.onEnvoye] appelé quand un canal est ouvert ou l'envoi parti
 * @param {() => Promise<object>} [props.envoiServeur] envoi par le serveur ; absent, le canal ne s'affiche pas
 * @param {string} [props.cibleServeur] adresse à laquelle le serveur enverra
 * @param {string} [props.serveurIndisponible] pourquoi le serveur ne peut pas envoyer cette pièce
 * @param {{label: string, onClick: () => void}} [props.emettre] sort la pièce du brouillon depuis
 *   la feuille, pour que SwissTech puisse l'envoyer
 * @param {{texte: string, action?: string, onAction?: () => void}} [props.avertissement] ce qui
 *   manque à la pièce avant de partir (l'IBAN du QR-facture, par exemple)
 * @param {'devis'|'facture'} [props.lienType] pièce dont le lien personnel du client
 *   accompagne le message WhatsApp, SMS ou e-mail
 * @param {string} [props.cheminPdf] PDF officiel, sous `/api`, que le partage du
 *   téléphone joint en fichier
 * @param {boolean} [props.pdfALaDemande] ne prépare le PDF que sur demande : celui
 *   d'une facture se fige au premier téléchargement, et ouvrir la feuille pour
 *   rien ne doit pas la figer
 * @param {() => Promise<{courrielConfigure?: boolean}>} [props.chargeReglages] dit si le
 *   serveur a une messagerie raccordée
 * @param {{type: 'DEVIS'|'FACTURE', id: number}} [props.notePartage] pièce dont un envoi par
 *   le téléphone (WhatsApp, SMS…) se note au journal des envois, comme un e-mail
 * @param {boolean} [props.joindreAttestations] propose, en tête de l'envoi d'une offre, les
 *   attestations de l'entreprise à joindre (le compte en a le droit)
 * @param {() => Promise<object[]>} [props.chargeAttestations] les attestations de l'entreprise
 * @param {() => Promise<{attestations: number[]}>} [props.chargeChoixAttestations] celles déjà
 *   retenues pour l'offre
 * @param {(ids: number[]) => Promise<object>} [props.retiensAttestations] retient le choix sur
 *   l'offre, pour la page que le lien ouvre
 * @returns {JSX.Element} la feuille
 */
export function MenuEnvoi({
  piece,
  nature,
  client,
  soc,
  onClose,
  onEnvoye,
  notePartage,
  envoiServeur,
  cibleServeur,
  serveurIndisponible,
  emettre,
  avertissement,
  lienType,
  cheminPdf,
  versionPdf = '',
  pdfALaDemande,
  relance,
  chargeReglages = () => envoisApi.reglages(),
  chargeBoite = () => envoisApi.boite(),
  joindreAttestations = false,
  chargeAttestations = () => piecesApi.liste('ENTREPRISE', soc?.id),
  chargeChoixAttestations = () => envoisApi.attestationsDeLOffre(piece?.id),
  retiensAttestations = (ids) => envoisApi.retiensAttestations(piece?.id, ids),
}) {
  // « 📎 Joindre des attestations » : la RC, le registre du commerce… cochés
  // ici partent avec l'offre — joints à l'e-mail, et montrés sur la page que
  // le lien WhatsApp ou SMS ouvre. Seulement pour une offre enregistrée, hors
  // relance, et pour qui a le droit de les voir.
  const peutJoindre =
    !!joindreAttestations && lienType === 'devis' && !relance && Number.isInteger(piece?.id);
  const [disponibles, setDisponibles] = useState([]);
  const [coches, setCoches] = useState([]);
  // « pret » : le choix est celui de l'offre au serveur ; « enregistrement » :
  // il part ; « echec » : il n'a pas pu être retenu.
  const [choixEtat, setChoixEtat] = useState('pret');
  const touche = useRef(false);
  const versionChoix = useRef(0);
  const retiens = (ids) => {
    const n = ++versionChoix.current;
    setChoixEtat('enregistrement');
    Promise.resolve()
      .then(() => retiensAttestations(ids))
      .then(() => {
        if (n === versionChoix.current) setChoixEtat('pret');
      })
      .catch(() => {
        if (n === versionChoix.current) setChoixEtat('echec');
      });
  };
  useEffect(() => {
    if (!peutJoindre) return undefined;
    let vivant = true;
    Promise.all([
      Promise.resolve().then(chargeAttestations),
      Promise.resolve()
        .then(chargeChoixAttestations)
        .catch(() => null),
    ])
      .then(([pieces, choix]) => {
        if (!vivant) return;
        const dispo = attestationsAEnvoyer(Array.isArray(pieces) ? pieces : [], soc?.forme);
        setDisponibles(dispo);
        if (touche.current) return;
        const retenues = Array.isArray(choix?.attestations) ? choix.attestations : [];
        const ids = retenues.filter((id) => dispo.some((p) => p.id === id));
        setCoches(ids);
        // Une attestation remplacée depuis par une plus récente restait sur
        // la page du client sans être cochée ici : le choix se remet d'accord.
        if (ids.length !== retenues.length) retiens(ids);
      })
      .catch(() => {});
    return () => {
      vivant = false;
    };
    // Une lecture à l'ouverture de la feuille.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const avecAttestations = peutJoindre && disponibles.length > 0;
  const nbJointes = avecAttestations ? coches.length : 0;
  const basculeAttestation = (id) => {
    touche.current = true;
    const ids = coches.includes(id) ? coches.filter((x) => x !== id) : [...coches, id];
    setCoches(ids);
    retiens(ids);
  };
  // Le lien ne part pas tant que le choix n'est pas retenu : le client
  // ouvrirait une page sans les documents cochés. L'e-mail, lui, porte le
  // choix et le retient lui-même ; il attend seulement l'enregistrement en
  // cours, pour qu'un choix plus ancien n'arrive pas après lui.
  const choixEnCours = avecAttestations && choixEtat === 'enregistrement';
  const lienBloque = avecAttestations && choixEtat !== 'pret';
  // Sans messagerie raccordée (SMTP_HOTE vide), le serveur inscrit le courriel
  // au journal sans l'envoyer, et l'écran disait « Envoyé » : le client ne
  // recevait rien. La feuille le sait désormais et oriente vers les autres
  // canaux. Une réponse absente (droits, réseau) laisse le bouton tel quel.
  const [messagerie, setMessagerie] = useState(null);
  useEffect(() => {
    if (!envoiServeur) return undefined;
    let vivant = true;
    Promise.resolve()
      .then(chargeReglages)
      .then((r) => {
        if (vivant && typeof r?.courrielConfigure === 'boolean') setMessagerie(r.courrielConfigure);
      })
      .catch(() => {});
    return () => {
      vivant = false;
    };
    // Une lecture à l'ouverture de la feuille.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // La boîte e-mail de l'entreprise (info@bektech.ch) : raccordée, tout
  // e-mail part par SwissTech, de son adresse — plus par l'application Mail
  // du téléphone, qui enverrait d'une autre adresse.
  const [boite, setBoite] = useState(null);
  useEffect(() => {
    if (!envoiServeur) return undefined;
    let vivant = true;
    Promise.resolve()
      .then(chargeBoite)
      .then((b) => {
        if (vivant && b?.raccordee) setBoite(b);
      })
      .catch(() => {});
    return () => {
      vivant = false;
    };
    // Une lecture à l'ouverture de la feuille.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const sansMessagerie = !!envoiServeur && messagerie === false && !boite;
  const envoiParServeur = sansMessagerie ? null : envoiServeur;
  const raisonServeur = sansMessagerie
    ? 'Pas encore raccordé : la messagerie de SwissTech n’est pas configurée. Envoie avec « Envoyer le PDF », E-mail ou WhatsApp ci-dessous.'
    : serveurIndisponible;
  // Le message de WhatsApp ou du SMS partait sans la pièce : le lien
  // personnel du client l'accompagne désormais, et le PDF lui-même part par
  // le partage du téléphone.
  // Plusieurs demandes : la facture qu'on vient d'émettre depuis cette feuille
  // n'est enregistrée au serveur qu'un instant après, et la première demande
  // de son lien était refusée — le message partait alors sans le lien.
  const [versionLien, setVersionLien] = useState(0);
  const {
    lien,
    pret,
    echec: lienEchoue,
  } = useLienClient(lienType || 'devis', lienType ? piece?.id : undefined, versionLien, 4);
  const lienEnAttente = !!lienType && Number.isInteger(piece?.id) && !pret;
  // Une pièce qui doit partir avec son lien ne part jamais sans : un brouillon
  // (à émettre d'abord) ou un lien que le serveur n'a pas donné ne propose
  // plus WhatsApp ni « Autre » avec un message vide de la pièce.
  // L'aperçu que WhatsApp montre (la feuille A4) se prépare dès que le lien
  // est là : WhatsApp le lit au moment de l'envoi et n'attend pas. Le premier
  // aperçu d'une pièce, à dessiner, prenait plusieurs secondes — le message
  // partait alors sans la feuille.
  useEffect(() => {
    const adresse = lienType ? adresseApercuLien(lien?.url) : null;
    if (adresse && typeof Image === 'function') new Image().src = adresse;
  }, [lienType, lien?.url]);
  const brouillon = !!emettre;
  const lienManquant = !!lienType && Number.isInteger(piece?.id) && lienEchoue;
  const sansLien = brouillon || lienManquant;
  const contenu = composeMessage(piece, nature, soc, lien?.url, client?.nom, relance);
  const peutPartager =
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function';
  // Deux façons d'envoyer, pas davantage : l'e-mail par SwissTech, et le PDF
  // partagé par le téléphone (WhatsApp, Mail, Messages… au choix). Les liens
  // WhatsApp, SMS et e-mail séparés doublaient ce partage ; ils ne restent que
  // là où le téléphone ne sait pas partager un fichier (ordinateur), ou tant
  // que la pièce n'a pas de PDF. L'application Mail n'est jamais proposée
  // quand SwissTech peut envoyer : elle partait d'une autre adresse (Gmail…).
  // Avec le lien de la pièce, c'est lui qui part : WhatsApp, Messages ou
  // Telegram en montrent l'aperçu (la feuille A4), et chaque ouverture, chaque
  // téléchargement, chaque transmission passe par SwissTech et se compte. Un
  // fichier PDF partagé s'ouvrait sur le téléphone du client sans que personne
  // le sache. Le fichier ne part plus que pour une pièce sans lien.
  const parLien = !!lien?.url && peutPartager;
  const parPartage = !!cheminPdf && peutPartager && !parLien && !lienType;
  const liens =
    parPartage || parLien || sansLien
      ? []
      : liensEnvoi(client || {}, contenu).filter(
          (l) => l.cle !== 'sms' && !((boite || envoiParServeur) && l.cle === 'email'),
        );
  const [pdf, setPdf] = useState({ fichier: null, pret: !cheminPdf, demande: !pdfALaDemande });

  // Le PDF se prépare avant le geste d'envoi : le partage doit partir dans le
  // geste même du doigt, que l'iPhone refuse après une attente. Celui d'une
  // offre se prépare dès l'ouverture ; celui d'une facture, qui se fige au
  // premier téléchargement, seulement quand on le demande.
  useEffect(() => {
    // Le fichier ne sert qu'au partage d'une pièce sans lien.
    if (!cheminPdf || !pdf.demande || !parPartage) return undefined;
    let annule = false;
    fichierDocumentPrepare(cheminPdf, versionPdf)
      .then((fichier) => {
        if (!annule)
          setPdf((p) => ({
            ...p,
            fichier: partagePossible(fichier) ? fichier : null,
            pret: true,
            echec: false,
          }));
      })
      .catch(() => {
        // L'échec se dit, avec de quoi réessayer : le bouton disparaissait
        // sans un mot.
        if (!annule) setPdf((p) => ({ ...p, fichier: null, pret: true, echec: true }));
      });
    return () => {
      annule = true;
    };
  }, [cheminPdf, versionPdf, pdf.demande, pdf.essai, parPartage]);
  // Le partage du téléphone ne dit pas quelle application a servi : on le
  // demande d'un toucher, pour noter « WhatsApp » au journal comme un e-mail.
  const [aNoter, setANoter] = useState(false);
  const peutNoter = !!notePartage && Number.isInteger(notePartage.id);
  const note = (canal) => {
    if (peutNoter) {
      envoisApi.noteUnPartage(notePartage.type, notePartage.id, canal).catch(() => {});
    }
  };
  const partagePdf = async () => {
    try {
      await navigator.share({ files: [pdf.fichier], title: contenu.titre, text: contenu.message });
    } catch {
      // Partage annulé : rien à signaler.
      return;
    }
    if (peutNoter) {
      setANoter(true);
      return;
    }
    if (onEnvoye) onEnvoye('pdf');
    onClose();
  };
  const partageLien = async () => {
    if (lienBloque) return;
    try {
      await navigator.share({ title: contenu.titre, text: contenu.message });
    } catch {
      // Partage annulé : rien à signaler.
      return;
    }
    if (peutNoter) {
      setANoter(true);
      return;
    }
    if (onEnvoye) onEnvoye('lien');
    onClose();
  };
  const choisitCanal = (canal) => {
    note(canal);
    if (onEnvoye) onEnvoye(canal === 'WHATSAPP' ? 'whatsapp' : 'pdf');
    onClose();
  };
  const [etat, setEtat] = useState('repos');
  const [message, setMessage] = useState('');
  const [copie, setCopie] = useState('');

  const style = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 13,
    border: '1px solid ' + C.border,
    borderRadius: 11,
    marginBottom: 9,
    textDecoration: 'none',
    color: C.ink,
  };

  const envoieParLeServeur = async () => {
    if (etat === 'envoi' || choixEnCours) return;
    setEtat('envoi');
    setMessage('');
    try {
      const ligne = await envoiParServeur(avecAttestations ? { attestations: coches } : undefined);
      setEtat('envoye');
      setMessage('Envoyé à ' + (ligne?.destinataire || cibleServeur || 'l’adresse du client'));
      if (onEnvoye) onEnvoye('serveur');
      // Le message est parti : la feuille se referme d'elle-meme, le temps de
      // lire la confirmation.
      setTimeout(onClose, 1200);
    } catch (e) {
      setEtat('echec');
      setMessage(e?.message || 'L’envoi a échoué.');
    }
  };

  // Telegram, Viber, Signal… n'ont pas de lien d'ouverture commun : le
  // partage du telephone laisse choisir l'application. Sans lui (ordinateur,
  // navigateur ancien), le message est copie pour etre colle a la main.
  const partageAilleurs = async () => {
    // Le lien n'est pas encore là : le message partirait sans la pièce.
    if (lienEnAttente || lienBloque) return;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: contenu.titre, text: contenu.message });
      } catch {
        // Partage annule par l'utilisateur : rien a signaler.
        return;
      }
      note('PARTAGE');
      if (onEnvoye) onEnvoye('autre');
      onClose();
      return;
    }
    try {
      await navigator.clipboard.writeText(contenu.message);
      setCopie(
        'Message copié — collez-le dans une autre app (Telegram, Viber, Signal, Messenger…).',
      );
      if (onEnvoye) onEnvoye('autre');
    } catch {
      setCopie('La copie a échoué : sélectionne le message dans ton application.');
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.6)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.white,
          borderRadius: '16px 16px 0 0',
          padding: 18,
          width: '100%',
          maxWidth: 440,
        }}
      >
        {aNoter ? (
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, textAlign: 'center' }}>
              {trf('✓ {0} partagée', contenu.titre)}
            </div>
            <div
              style={{ fontSize: 11.5, color: C.muted, textAlign: 'center', margin: '4px 0 12px' }}
            >
              {tr('Par où l’as-tu envoyée ? (pour la noter)')}
            </div>
            {[
              ['WHATSAPP', '🟢', 'WhatsApp'],
              ['SMS', '💬', 'SMS / Messages'],
              ['EMAIL', '✉️', 'Mail du téléphone'],
              ['PARTAGE', '📎', 'Autre'],
            ].map(([canal, icone, nom]) => (
              <button
                key={canal}
                type="button"
                onClick={() => choisitCanal(canal)}
                style={{
                  ...style,
                  width: '100%',
                  background: C.white,
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 20 }}>{icone}</span> {tr(nom)}
              </button>
            ))}
          </div>
        ) : (
          <>
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 4, textAlign: 'center' }}>
              {relance ? tr('🔔 Relancer le client') : tr('📤 Envoyer au client')}
            </div>
            <div
              style={{
                fontSize: 11,
                color: C.muted,
                marginBottom: 13,
                textAlign: 'center',
              }}
            >
              {contenu.titre} · tu relis avant d&apos;envoyer
            </div>
            {avecAttestations && (
              <div
                role="group"
                aria-label={tr('Joindre des attestations')}
                style={{
                  border: '1px solid ' + C.border,
                  borderRadius: 11,
                  padding: '10px 12px 8px',
                  marginBottom: 9,
                }}
              >
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    color: C.grey,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    marginBottom: 2,
                  }}
                >
                  📎 {tr('Joindre des attestations')}
                </div>
                {disponibles.map((p) => (
                  <label
                    key={p.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 0',
                      borderTop: '1px solid #eef2f6',
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: C.ink,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={coches.includes(p.id)}
                      onChange={() => basculeAttestation(p.id)}
                      style={{
                        width: 20,
                        height: 20,
                        margin: 0,
                        flexShrink: 0,
                        accentColor: C.teal,
                      }}
                    />
                    <span style={{ minWidth: 0 }}>
                      {iconeAttestation(p.categorie)} {tr(nomAttestation(p, soc?.forme))}
                    </span>
                  </label>
                ))}
                <div style={{ fontSize: 10, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>
                  {choixEtat === 'enregistrement'
                    ? tr('Enregistrement…')
                    : tr(
                        'Coché → le PDF part avec l’offre, dans le même e-mail ou sur la page du lien',
                      )}
                </div>
                {choixEtat === 'echec' && (
                  <div
                    role="alert"
                    onClick={() => retiens(coches)}
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.red,
                      marginTop: 4,
                      cursor: 'pointer',
                    }}
                  >
                    ✕ {tr('Le choix n’a pas pu être enregistré : touche ici pour réessayer.')}
                  </div>
                )}
              </div>
            )}
            {avertissement && (
              <div
                role="alert"
                style={{
                  border: '1.5px solid ' + C.orange,
                  background: C.orangeL,
                  borderRadius: 11,
                  padding: 12,
                  marginBottom: 9,
                  fontSize: 11.5,
                  color: C.ink,
                  lineHeight: 1.45,
                }}
              >
                ⚠️ {avertissement.texte}
                {avertissement.action && avertissement.onAction && (
                  <button
                    onClick={avertissement.onAction}
                    style={{
                      display: 'block',
                      marginTop: 8,
                      border: 'none',
                      borderRadius: 9,
                      padding: '8px 12px',
                      background: C.orange,
                      color: '#fff',
                      fontSize: 11.5,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    {avertissement.action}
                  </button>
                )}
              </div>
            )}
            {(envoiParServeur || raisonServeur) && (
              <div
                role="button"
                aria-disabled={!envoiParServeur || etat === 'envoi'}
                onClick={envoiParServeur ? envoieParLeServeur : undefined}
                style={{
                  ...style,
                  cursor: envoiParServeur ? 'pointer' : 'default',
                  border: '1.5px solid ' + (envoiParServeur ? C.teal : C.border),
                  background: envoiParServeur ? C.tealL : C.navyXL,
                  opacity: etat === 'envoi' ? 0.7 : 1,
                }}
              >
                <span style={{ fontSize: 22 }}>📨</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>
                    {etat === 'envoi'
                      ? 'Envoi en cours…'
                      : nbJointes === 1
                        ? tr('Envoyer par e-mail · offre + 1 document')
                        : nbJointes > 1
                          ? trf('Envoyer par e-mail · offre + {0} documents', nbJointes)
                          : 'Envoyer par e-mail depuis SwissTech'}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {raisonServeur ||
                      (boite ? 'De ' + boite.adresse + ' · ' : '') +
                        'PDF officiel joint · ' +
                        (cibleServeur || 'adresse de la fiche client')}
                  </div>
                  {!envoiParServeur && emettre && (
                    <button
                      onClick={emettre.onClick}
                      style={{
                        display: 'block',
                        marginTop: 8,
                        border: 'none',
                        borderRadius: 9,
                        padding: '8px 12px',
                        background: C.green,
                        color: '#fff',
                        fontSize: 11.5,
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      {tr(emettre.label)}
                    </button>
                  )}
                  {envoiParServeur && !boite && messagerie !== null && (
                    <div style={{ fontSize: 11, color: C.ink, marginTop: 4, lineHeight: 1.4 }}>
                      {trf(
                        'Pour qu’il parte de votre adresse ({0}), raccordez votre boîte e-mail : Réglages → E-mail et envois.',
                        soc?.email || 'info@…',
                      )}
                    </div>
                  )}
                  {message && (
                    <div
                      role="status"
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        marginTop: 3,
                        color: etat === 'echec' ? C.red : C.green,
                      }}
                    >
                      {etat === 'echec' ? '✕ ' : '✓ '}
                      {message}
                    </div>
                  )}
                </div>
              </div>
            )}
            {(parLien || (lienType && peutPartager && !lienManquant)) && (
              <div
                role="button"
                aria-disabled={!parLien || lienBloque}
                onClick={parLien && !lienBloque ? partageLien : undefined}
                style={{
                  ...style,
                  cursor: parLien && !lienBloque ? 'pointer' : 'default',
                  border: '1.5px solid ' + C.green,
                  background: C.greenL,
                  opacity: parLien && !lienBloque ? 1 : 0.6,
                }}
              >
                <span style={{ fontSize: 22 }}>📲</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>
                    {!parLien
                      ? tr('Préparation du lien…')
                      : choixEnCours
                        ? tr('Enregistrement…')
                        : tr('Envoyer par WhatsApp, SMS, Messages…')}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {tr('La pièce s’affiche comme une feuille A4 · chaque ouverture est suivie')}
                  </div>
                </div>
              </div>
            )}
            {parPartage && cheminPdf && !pdf.demande && (
              <div
                role="button"
                onClick={() => setPdf((p) => ({ ...p, demande: true }))}
                style={{
                  ...style,
                  cursor: 'pointer',
                  border: '1.5px solid ' + C.green,
                  background: C.greenL,
                }}
              >
                <span style={{ fontSize: 22 }}>📎</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>Préparer le PDF à envoyer</div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    puis WhatsApp, Mail, Messages… le fichier part joint
                  </div>
                </div>
              </div>
            )}
            {parPartage && cheminPdf && pdf.demande && (pdf.fichier || !pdf.pret) && (
              <div
                role="button"
                aria-disabled={!pdf.fichier}
                onClick={pdf.fichier ? partagePdf : undefined}
                style={{
                  ...style,
                  cursor: pdf.fichier ? 'pointer' : 'default',
                  border: '1.5px solid ' + C.green,
                  background: C.greenL,
                  opacity: pdf.fichier ? 1 : 0.6,
                }}
              >
                <span style={{ fontSize: 22 }}>📎</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>
                    {pdf.fichier ? 'Envoyer le PDF' : 'Préparation du PDF…'}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    WhatsApp, Mail, Messages… le fichier part joint
                  </div>
                </div>
              </div>
            )}
            {parPartage && cheminPdf && pdf.demande && pdf.echec && (
              <div
                role="button"
                data-testid="pdf-reessayer"
                onClick={() =>
                  setPdf((p) => ({
                    fichier: null,
                    pret: false,
                    demande: true,
                    echec: false,
                    essai: (p.essai || 0) + 1,
                  }))
                }
                style={{
                  ...style,
                  cursor: 'pointer',
                  border: '1.5px solid ' + C.amber,
                  background: C.amberL,
                }}
              >
                <span style={{ fontSize: 22 }}>🔄</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>
                    {tr('Le PDF n’a pas pu être préparé : réessayer')}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {tr('Vérifie la connexion, puis touche ici.')}
                  </div>
                </div>
              </div>
            )}
            {liens.map((l) => (
              <a
                key={l.cle}
                href={l.href}
                onClick={(e) => {
                  // Le lien du client n'est pas encore prêt : le message partirait
                  // sans la pièce, comme avant.
                  if (lienEnAttente || lienBloque) {
                    e.preventDefault();
                    return;
                  }
                  // Le canal est ouvert, pas l'envoi confirme : l'artisan relit et
                  // touche « envoyer » dans son application. La piece est donc
                  // marquee comme partie, ce qui est ce que l'ecran peut savoir.
                  note(CANAL_DU_LIEN[l.cle] || 'PARTAGE');
                  if (onEnvoye) {
                    onEnvoye(l.cle);
                  }
                  onClose();
                }}
                style={style}
                {...(l.externe ? { target: '_blank', rel: 'noreferrer' } : {})}
              >
                <span style={{ fontSize: 22 }}>{l.icone}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>{l.nom}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {lienEnAttente
                      ? 'Préparation du lien…'
                      : choixEnCours
                        ? tr('Enregistrement…')
                        : l.cible}
                    {lien?.url && !lienEnAttente ? ' · avec le lien de la pièce' : ''}
                  </div>
                </div>
              </a>
            ))}
            {brouillon && (
              <div style={{ ...style, background: C.navyXL }}>
                <span style={{ fontSize: 22 }}>📲</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>
                    {tr('Envoyer par WhatsApp, SMS, Messages…')}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {tr('Émets d’abord la facture : le message partira avec son lien.')}
                  </div>
                </div>
              </div>
            )}
            {lienManquant && (
              <div
                role="button"
                data-testid="lien-reessayer"
                onClick={() => setVersionLien((v) => v + 1)}
                style={{
                  ...style,
                  cursor: 'pointer',
                  border: '1.5px solid ' + C.amber,
                  background: C.amberL,
                }}
              >
                <span style={{ fontSize: 22 }}>🔄</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>
                    {tr('Le lien n’a pas pu être préparé : réessayer')}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {tr('Vérifie la connexion, puis touche ici.')}
                  </div>
                </div>
              </div>
            )}
            {!parPartage && !parLien && !sansLien && (
              <div role="button" onClick={partageAilleurs} style={{ ...style, cursor: 'pointer' }}>
                <span style={{ fontSize: 22 }}>📲</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>Autre</div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    Telegram, Viber, Signal… (choisir l’app)
                  </div>
                  {copie && (
                    <div
                      role="status"
                      style={{ fontSize: 11, fontWeight: 700, marginTop: 3, color: C.green }}
                    >
                      ✓ {copie}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div
              onClick={onClose}
              style={{
                textAlign: 'center',
                padding: 11,
                fontSize: 11.5,
                fontWeight: 700,
                color: C.muted,
                cursor: 'pointer',
              }}
            >
              Annuler
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Canal du journal pour chaque lien direct (ordinateur, pièce sans PDF). */
const CANAL_DU_LIEN = { whatsapp: 'WHATSAPP', sms: 'SMS', email: 'EMAIL' };
